import React, { useEffect, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import classnames from "classnames"
import { getContrastTextColor, isLightColor } from '@utils'
import Tiktok from "@src/assets/images/icons/tik-tok.svg"
import {
    Card,
    CardBody,
    Spinner
} from "reactstrap"
import {
    Facebook,
    Linkedin,
    Instagram,
    Globe,
    Tag,
    FileText,
    Image as ImageIcon,
    ArrowRight,
    ArrowLeft,
    Check,
    Copy,
    ExternalLink,
    User,
    Video,
    Share2
} from "react-feather"
import RequestsForParticipation from "@src/views/admin/manageBrands/requestsForParticipation"
import { getBrandProfileDetail } from "@src/views/tables/data-tables-admin-brands/store/actions"
import ImageViewer from "@src/views/components/ImageViewer"
import PdfViewer from "@src/views/components/PdfViewer"
import VideoPlayer from "@src/views/components/VideoPlayer"
import ShareProfileModal from "@src/views/components/ShareProfileModal"
import "@src/views/rds/brands/BrandProfile.scss"

const ViewBrandProfile = () => {
    const [mainTab, setMainTab] = useState("profile") // "profile" | "participation"
    const [profileInfo, setProfileInfo] = useState(null)
    const [isPageLoading, setIsPageLoading] = useState(true)
    const [isShareModalOpen, setIsShareModalOpen] = useState(false)

    const [activeNavTab, setActiveNavTab] = useState('Overview')
    const [activeResourceSubTab, setActiveResourceSubTab] = useState('learn')

    const [imageViewer, setImageViewer] = useState({ show: false, src: '' })
    const [pdfViewer, setPdfViewer] = useState({ show: false, url: '', filename: '' })
    const [videoPlayer, setVideoPlayer] = useState({ show: false, url: '', filename: '' })

    const { id } = useParams()
    const brandProfileSelector = useSelector(
        (store) => store.dataTablesAdminBrands
    )
    const dispatch = useDispatch()

    useEffect(() => {
        if (!!brandProfileSelector?.brandProfile) {
            setProfileInfo(brandProfileSelector?.brandProfile)
            setIsPageLoading(false)
        }
    }, [brandProfileSelector])

    useEffect(() => {
        setIsPageLoading(true)
        dispatch(getBrandProfileDetail(id))
    }, [])

    const [copiedRdCode, setCopiedRdCode] = useState(false)
    const [copiedClientCode, setCopiedClientCode] = useState(false)

    const handleCopyCode = (code, type = 'rd') => {
        if (navigator.clipboard && code) {
            navigator.clipboard.writeText(code)
            if (type === 'client') {
                setCopiedClientCode(true)
                setTimeout(() => setCopiedClientCode(false), 2500)
            } else {
                setCopiedRdCode(true)
                setTimeout(() => setCopiedRdCode(false), 2500)
            }
        }
    }

    const brandThemeColor = profileInfo?.brand_color || '#56B6FF'
    const brandSecondaryColor = profileInfo?.brand_secondary_color || ''
    const isPrimaryLight = isLightColor(brandThemeColor)
    const isSecondaryLight = brandSecondaryColor ? isLightColor(brandSecondaryColor) : false
    const secondaryTextColor = brandSecondaryColor ? getContrastTextColor(brandSecondaryColor) : undefined
    const secondaryBorder = isSecondaryLight ? '1px solid rgba(0, 0, 0, 0.15)' : undefined
    const secondaryBadgeStyle = (brandSecondaryColor && brandSecondaryColor.trim().toLowerCase() !== brandThemeColor.trim().toLowerCase()) ? {
        backgroundColor: brandSecondaryColor,
        color: secondaryTextColor,
        border: secondaryBorder
    } : undefined

    const specialDiets = profileInfo?.attributes?.special_diet || []
    const categories = profileInfo?.attributes?.category || []
    const values = profileInfo?.attributes?.values || []
    const discountCode = profileInfo?.discount_code || ''
    const clientDiscountCode = profileInfo?.client_discount_code || ''
    const discountDetails = profileInfo?.discount_details || ''
    const shopWebsiteUrl = profileInfo?.shop_website || ''
    const hasDiscountCodes = Boolean(discountCode || clientDiscountCode)

    return (
        <div className="view-page">
            <div className="admin-top-header-bar">
                <Link to="/admin/manage-brands" className="admin-back-link">
                    <ArrowLeft size={14} className="back-icon" /> Back to Brands
                </Link>

                <div className="admin-segmented-tabs">
                    <button
                        type="button"
                        className={`tab-btn ${mainTab === 'profile' ? 'active' : ''}`}
                        onClick={() => setMainTab('profile')}
                    >
                        <User size={13} /> Brand Profile
                    </button>
                    <button
                        type="button"
                        className={`tab-btn ${mainTab === 'participation' ? 'active' : ''}`}
                        onClick={() => setMainTab('participation')}
                    >
                        <FileText size={13} /> Participation Form
                    </button>
                </div>
            </div>

            {isPageLoading && (
                <div className="w-100 my-4 text-center">
                    <Spinner color="primary" />
                </div>
            )}

            {!isPageLoading && mainTab === 'profile' && profileInfo && (
                <div className="brand-profile-figma-v2">
                    {/* Top Hero Banner */}
                    <div className={classnames("brand-hero-wrapper", { "is-light-theme": isPrimaryLight })} style={{ backgroundColor: brandThemeColor }}>
                        {/* Top action row */}
                        <div className="hero-top-row">
                            <div className="hero-identity">
                                <div className="brand-circle-logo">
                                    {profileInfo?.company_logo || profileInfo?.profile_image ? (
                                        <img src={profileInfo.company_logo || profileInfo.profile_image} alt={profileInfo?.brand_name || profileInfo?.name} />
                                    ) : (
                                        <span style={{ color: isPrimaryLight ? '#0f172a' : brandThemeColor }}>
                                            {(profileInfo?.brand_name || profileInfo?.name || 'B').substring(0, 3)}
                                        </span>
                                    )}
                                </div>
                                <div className="brand-info">
                                    <h1 className="brand-name-heading">{profileInfo?.brand_name || profileInfo?.name || ''}</h1>
                                    {!!(profileInfo?.tagline || profileInfo?.person_title) && (
                                        <p className="brand-tagline-text">
                                            {profileInfo?.tagline || profileInfo?.person_title}
                                        </p>
                                    )}
                                    <div className="brand-pill-tags">
                                        {categories.map((item, idx) => (
                                            <span
                                                className="pill-tag"
                                                key={`cat-${idx}`}
                                                style={secondaryBadgeStyle}
                                            >
                                                {item.value}
                                            </span>
                                        ))}
                                        {specialDiets.map((item, idx) => (
                                            <span
                                                className="pill-tag"
                                                key={`diet-${idx}`}
                                                style={secondaryBadgeStyle}
                                            >
                                                {item.value}
                                            </span>
                                        ))}
                                        {values.map((item, idx) => (
                                            <span
                                                className="pill-tag"
                                                key={`val-${idx}`}
                                                style={secondaryBadgeStyle}
                                            >
                                                {item.value}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <div className="hero-action-btns">
                                <button
                                    type="button"
                                    className="btn-hero-share"
                                    onClick={() => setIsShareModalOpen(true)}
                                    title="Share Brand Profile"
                                >
                                    <Share2 size={14} /> Share Profile
                                </button>
                            </div>
                        </div>

                        {/* Social icons row directly underneath the logo circle */}
                        {(profileInfo.instagram_url || profileInfo.facebook_url || profileInfo.twitter_url || profileInfo.tiktok_url || profileInfo.pinterest_url || profileInfo.linkedin_url || profileInfo.website) && (
                            <div className="hero-social-row">
                                {!!profileInfo.instagram_url && (
                                    <a href={profileInfo.instagram_url} target="_blank" rel="noreferrer" title="Instagram">
                                        <Instagram size={16} />
                                    </a>
                                )}
                                {!!profileInfo.facebook_url && (
                                    <a href={profileInfo.facebook_url} target="_blank" rel="noreferrer" title="Facebook">
                                        <Facebook size={16} />
                                    </a>
                                )}
                                {!!profileInfo.twitter_url && (
                                    <a href={profileInfo.twitter_url} target="_blank" rel="noreferrer" title="X (Twitter)">
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                                        </svg>
                                    </a>
                                )}
                                {!!profileInfo.tiktok_url && (
                                    <a href={profileInfo.tiktok_url} target="_blank" rel="noreferrer" title="TikTok">
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 3 15.68 6.34 6.34 0 0 0 9.34 22a6.34 6.34 0 0 0 6.34-6.32V8.71a8.21 8.21 0 0 0 4.81 1.55v-3.45a4.85 4.85 0 0 1-.9-.12z" />
                                        </svg>
                                    </a>
                                )}
                                {!!profileInfo.pinterest_url && (
                                    <a href={profileInfo.pinterest_url} target="_blank" rel="noreferrer" title="Pinterest">
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345-.09.375-.291 1.199-.334 1.357-.053.225-.172.271-.401.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.354-.629-2.758-1.379l-.749 2.848c-.269 1.045-1.004 2.352-1.498 3.146 1.123.345 2.306.535 3.546.535 6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z" />
                                        </svg>
                                    </a>
                                )}
                                {!!profileInfo.linkedin_url && (
                                    <a href={profileInfo.linkedin_url} target="_blank" rel="noreferrer" title="LinkedIn">
                                        <Linkedin size={16} />
                                    </a>
                                )}
                                {!!profileInfo.website && (
                                    <a href={profileInfo.website} target="_blank" rel="noreferrer" title="Website">
                                        <Globe size={16} />
                                    </a>
                                )}
                            </div>
                        )}

                        {/* Navigation Tabs directly inside the dark hero banner */}
                        <div className="hero-nav-tabs">
                            {['Overview', 'Programs', 'Assets', 'About'].map((tab) => (
                                <button
                                    key={tab}
                                    className={`hero-tab-item ${activeNavTab === tab ? 'active' : ''}`}
                                    onClick={() => setActiveNavTab(tab)}
                                >
                                    {tab}
                                    {activeNavTab === tab && <div className="active-indicator" />}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Main Content Area below Hero */}
                    <div className="brand-content-body">
                        {/* 1. ABOUT CARD */}
                        {(activeNavTab === 'Overview' || activeNavTab === 'About') && (
                            <div className="figma-card about-card">
                                <div className="card-header-label">ABOUT {profileInfo?.brand_name || profileInfo?.name || 'BRAND'}</div>
                                {profileInfo?.about_company ? (
                                    <div
                                        className="about-body-text"
                                        dangerouslySetInnerHTML={{
                                            __html: profileInfo.about_company
                                        }}
                                    />
                                ) : (
                                    <div className="about-body-text text-muted" style={{ fontStyle: 'italic' }}>
                                        No company description provided yet.
                                    </div>
                                )}
                                <div className="detail-table">
                                    <div className="detail-row">
                                        <div className="row-label">Category</div>
                                        <div className="row-value">
                                            {categories.length > 0 ? categories.map(c => c.value).join(' · ') : '-'}
                                        </div>
                                    </div>
                                    <div className="detail-row">
                                        <div className="row-label">Special diet</div>
                                        <div className="row-value">
                                            {specialDiets.length > 0 ? specialDiets.map(s => s.value).join(' · ') : '-'}
                                        </div>
                                    </div>
                                    <div className="detail-row">
                                        <div className="row-label">Certifications</div>
                                        <div className="row-value">
                                            {values.length > 0 ? values.map(v => v.value).join(' · ') : '-'}
                                        </div>
                                    </div>
                                    {profileInfo?.location && (
                                        <div className="detail-row">
                                            <div className="row-label">Location</div>
                                            <div className="row-value">
                                                {[
                                                    profileInfo?.location?.city?.value || profileInfo?.location?.city,
                                                    profileInfo?.location?.state?.map?.(s => s.label || s.value || s)?.join(', ') || profileInfo?.location?.state?.value || profileInfo?.location?.state,
                                                    profileInfo?.location?.country?.value || profileInfo?.location?.country
                                                ].filter(Boolean).join(', ') || 'N/A'}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* 2. RESOURCES CARD */}
                        {(activeNavTab === 'Overview' || activeNavTab === 'Assets') && (
                            <div className="figma-card resources-card">
                                <div className="resources-subnav-row">
                                    <button
                                        className={`subnav-tab ${activeResourceSubTab === 'learn' ? 'active' : ''}`}
                                        onClick={() => setActiveResourceSubTab('learn')}
                                    >
                                        Learn about the brand
                                    </button>
                                    <button
                                        className={`subnav-tab ${activeResourceSubTab === 'practice' ? 'active' : ''}`}
                                        onClick={() => setActiveResourceSubTab('practice')}
                                    >
                                        Use in your practice
                                    </button>
                                </div>

                                <div className="resource-color-indicators">
                                    <div className="indicator-pill green">
                                        <span className="dot green"></span>
                                        <span>Brand education</span>
                                    </div>
                                    <div className="indicator-pill purple">
                                        <span className="dot purple"></span>
                                        <span>Practice resources</span>
                                    </div>
                                </div>

                                <div className="resources-four-grid">
                                    {profileInfo?.media && profileInfo.media.length > 0 ? (
                                        (() => {
                                            const filteredMedia = profileInfo.media.filter((media, idx) => {
                                                const isEducation = media?.category === 'education' || media?.resource_type === 'education' || idx % 2 === 0
                                                if (activeResourceSubTab === 'learn') return isEducation
                                                if (activeResourceSubTab === 'practice') return !isEducation
                                                return true
                                            })
                                            if (filteredMedia.length === 0) {
                                                return (
                                                    <div className="resources-empty-state-box">
                                                        <div className="empty-icon-bubble">
                                                            <FileText size={16} />
                                                        </div>
                                                        <div className="empty-text-wrap">
                                                            <span className="empty-heading">
                                                                {activeResourceSubTab === 'learn' ? 'No Brand Education resources uploaded yet.' : 'No Practice resources uploaded yet.'}
                                                            </span>
                                                            <span className="empty-desc">
                                                                {activeResourceSubTab === 'learn'
                                                                    ? 'Educational documents and research materials will appear here.'
                                                                    : 'Practice handouts and client guides will appear here.'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                )
                                            }
                                            return filteredMedia.map((media, idx) => {
                                                const type = media?.image_type?.toLowerCase() || ''
                                                const isEducation = activeResourceSubTab === 'learn' ? true : activeResourceSubTab === 'practice' ? false : (idx % 2 === 0)
                                                return (
                                                    <div
                                                        className="resource-item-box"
                                                        key={idx}
                                                        onClick={() => {
                                                            if (type === 'pdf') {
                                                                setPdfViewer({ show: true, url: media.image_url, filename: media.product_image })
                                                            } else if (type === 'mp4') {
                                                                setVideoPlayer({ show: true, url: media.image_url, filename: media.product_image })
                                                            } else {
                                                                setImageViewer({ show: true, src: media.image_url })
                                                            }
                                                        }}
                                                    >
                                                        <div className="item-preview-top">
                                                            {type === 'pdf' ? (
                                                                <FileText size={28} />
                                                            ) : type === 'mp4' ? (
                                                                <Video size={28} />
                                                            ) : (
                                                                <ImageIcon size={28} />
                                                            )}
                                                        </div>
                                                        <div className="item-meta-bottom">
                                                            <div className="item-title text-truncate">{media.product_image || 'Resource Document'}</div>
                                                            <div className={`item-type ${isEducation ? 'education' : 'practice'}`}>
                                                                {isEducation ? 'Brand education' : 'Use in practice'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )
                                            })
                                        })()
                                    ) : (
                                        <div className="resources-empty-state-box">
                                            <div className="empty-icon-bubble">
                                                <FileText size={16} />
                                            </div>
                                            <div className="empty-text-wrap">
                                                <span className="empty-heading">
                                                    {activeResourceSubTab === 'learn' ? 'No Brand Education resources uploaded yet.' : 'No Practice resources uploaded yet.'}
                                                </span>
                                                <span className="empty-desc">
                                                    {activeResourceSubTab === 'learn'
                                                        ? 'Educational documents and research materials will appear here.'
                                                        : 'Practice handouts and client guides will appear here.'}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* 3. DISCOUNT BANNER */}
                        {hasDiscountCodes && (activeNavTab === 'Overview' || activeNavTab === 'Programs') && (
                            <div className={classnames("figma-discount-card", { "is-light-theme": isPrimaryLight })} style={{ backgroundColor: brandThemeColor }}>
                                <div className="discount-info-side">
                                    <div className="discount-icon-bubble">
                                        <Tag size={20} />
                                    </div>
                                    <div className="discount-details-column">
                                        <div className="discount-eyebrow">RD EXCLUSIVE & CLIENT CODES</div>
                                        <div className="discount-codes-wrapper">
                                            {!!discountCode && (
                                                <div className="discount-code-row">
                                                    <span className="discount-label">RD Code:</span>
                                                    <span className="discount-promocode">{discountCode}</span>
                                                    <button className="btn-code-copy" onClick={() => handleCopyCode(discountCode, 'rd')} title="Copy RD code">
                                                        {copiedRdCode ? <Check size={12} /> : <Copy size={12} />}
                                                        <span className="copy-btn-text">{copiedRdCode ? 'Copied' : 'Copy'}</span>
                                                    </button>
                                                </div>
                                            )}
                                            {!!clientDiscountCode && (
                                                <div className="discount-code-row">
                                                    <span className="discount-label">Client Code:</span>
                                                    <span className="discount-promocode">{clientDiscountCode}</span>
                                                    <button className="btn-code-copy" onClick={() => handleCopyCode(clientDiscountCode, 'client')} title="Copy client code">
                                                        {copiedClientCode ? <Check size={12} /> : <Copy size={12} />}
                                                        <span className="copy-btn-text">{copiedClientCode ? 'Copied' : 'Copy'}</span>
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        {!!discountDetails && (
                                            <div className="discount-validity">
                                                {discountDetails.includes('|') ? (
                                                    discountDetails.split('|').map((part, pIdx) => (
                                                        <div key={pIdx} className={pIdx > 0 ? "mt-25" : ""}>
                                                            {part.trim()}
                                                        </div>
                                                    ))
                                                ) : (
                                                    discountDetails
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {!!shopWebsiteUrl && (
                                    <a
                                        href={shopWebsiteUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="btn-shop-website"
                                        style={secondaryBadgeStyle}
                                    >
                                        <ExternalLink size={13} /> Shop Website
                                    </a>
                                )}
                            </div>
                        )}

                        {/* 4. OPEN PROGRAMS */}
                        {(activeNavTab === 'Overview' || activeNavTab === 'Programs') && (
                            <div className="open-programs-wrapper">
                                <div className="programs-super-label">OPEN PROGRAMS</div>
                                <div className="programs-two-grid">
                                    {profileInfo?.campaigns && profileInfo.campaigns.length > 0 ? (
                                        profileInfo.campaigns.map((camp, idx) => (
                                            <div className="program-item-card admin-program-card" key={idx}>
                                                <div>
                                                    <div className={`program-kicker ${idx % 2 === 0 ? 'ambassador' : 'paid'}`}>
                                                        {camp.opportunity_for || (idx % 2 === 0 ? 'AMBASSADOR PROGRAM' : 'PAID OPPORTUNITY')}
                                                    </div>
                                                    <h3 className="program-heading">{camp.campaign_title}</h3>
                                                    <div className="program-details-line">
                                                        {camp.brand_name} · Active outreach program
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="w-100 text-center py-3 px-2 border rounded" style={{ backgroundColor: '#fcfdfe', borderColor: '#e2e8f0', borderRadius: '10px' }}>
                                            <div className="d-inline-flex align-items-center justify-content-center mb-1" style={{ width: '44px', height: '44px', borderRadius: '50%', backgroundColor: '#f1f5f9', color: '#64748b' }}>
                                                <Tag size={22} />
                                            </div>
                                            <h6 className="font-weight-bold mb-50" style={{ color: '#1e293b', fontSize: '14px' }}>
                                                No Active Outreach Programs
                                            </h6>
                                            <p className="text-muted mb-0 mx-auto" style={{ fontSize: '12.5px', maxWidth: '440px' }}>
                                                {profileInfo?.brand_name || profileInfo?.name || 'This brand'} does not have any active outreach campaigns published at this moment.
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Participation Form / Requests Tab Content */}
            {mainTab === 'participation' && (
                <Card className="mt-1 shadow-sm border-0" style={{ borderRadius: '12px', overflow: 'hidden' }}>
                    <CardBody className="p-2">
                        <RequestsForParticipation />
                    </CardBody>
                </Card>
            )}

            {/* Media Viewers */}
            <ImageViewer
                show={imageViewer.show}
                hide={() => setImageViewer({ show: false, src: '' })}
                images={[{ src: imageViewer.src }]}
            />
            <PdfViewer
                show={pdfViewer.show}
                url={pdfViewer.url}
                filename={pdfViewer.filename}
                hide={() => setPdfViewer({ show: false, url: '', filename: '' })}
            />
            <VideoPlayer
                show={videoPlayer.show}
                url={videoPlayer.url}
                filename={videoPlayer.filename}
                hide={() => setVideoPlayer({ show: false, url: '', filename: '' })}
            />

            {/* SHARE PROFILE MODAL */}
            <ShareProfileModal
                isOpen={isShareModalOpen}
                toggle={() => setIsShareModalOpen(!isShareModalOpen)}
                brandData={profileInfo}
            />
        </div>
    )
}

export default ViewBrandProfile
